"use client";

import { type ReactNode, useCallback } from "react";

import { getImageProps } from "next/image";

import {
  PictureBehaviourContext,
  type PictureSource,
  type PictureWrapperProps,
} from "@jti/core-components/components/PictureBehaviour";

import { convertDimensionToInt } from "@/utils/convertDimensionToInt";
import { isOptimizedMagentoImageUrl } from "@/utils/isOptimizedMagentoImageUrl";
import { removeUrlQueryParams } from "@/utils/removeUrlQueryParams";

import type { ImageDimensionsMap } from "../utils/getImageDimensionsFromUrls";

type PictureBehaviourContextProviderProps = {
  children: ReactNode;
  imageDimensionsDict?: ImageDimensionsMap;
  isNextImageDisabled: boolean;
};

const emptyImageDimensionsDict: ImageDimensionsMap = {};

function resolveDimensions({
  url,
  explicitWidth,
  explicitHeight,
  dict,
}: {
  url?: string;
  explicitWidth?: string | number;
  explicitHeight?: string | number;
  dict: ImageDimensionsMap;
}): { width: number; height: number } | null {
  // First check for explicit dimensions provided in fe-libs
  const propsWidth = convertDimensionToInt(explicitWidth);
  const propsHeight = convertDimensionToInt(explicitHeight);
  if (propsWidth && propsHeight) {
    return { width: propsWidth, height: propsHeight };
  }

  // Fallback to dimensions from the dictionary
  if (url) {
    const cleanUrl = removeUrlQueryParams(url);
    return cleanUrl ? (dict[cleanUrl] ?? null) : null;
  }

  return null;
}

function renderNativePicture(
  imageProps: JSX.IntrinsicElements["img"],
  sources: (PictureSource & { srcSet: string })[],
  className?: string
) {
  return (
    <picture className={className}>
      {sources.map((source, index) => (
        <source key={`${source.media}_${index}`} media={source.media} type={source.type} srcSet={source.srcSet} />
      ))}
      <img {...imageProps} alt={imageProps.alt ?? ""} />
    </picture>
  );
}

/**
 * Implementation is based on Next.js documentation "Art Direction"
 * https://nextjs.org/docs/app/api-reference/components/image#art-direction
 */
export const PictureBehaviourContextProvider = ({
  children,
  imageDimensionsDict,
  isNextImageDisabled,
}: PictureBehaviourContextProviderProps) => {
  const stableImageDimensionsDict = imageDimensionsDict ?? emptyImageDimensionsDict;

  const renderPicture = useCallback(
    ({ imageProps, className, sources }: PictureWrapperProps) => {
      const existingSources = sources.filter((source): source is PictureSource & { srcSet: string } => !!source.srcSet);

      // No valid sources - fallback to native picture tag
      if (existingSources.length === 0) {
        console.error("No valid image sources found", { imageProps });
        return renderNativePicture(imageProps, [], className);
      }

      // Check if Next/Image is disabled
      if (isNextImageDisabled) {
        return renderNativePicture(imageProps, existingSources, className);
      }

      // Skip /_next/image for assets already optimized at the Magento origin
      // (Cloudflare Image Resizing) so they are not optimized a second time.
      const isMagentoOptimized =
        isOptimizedMagentoImageUrl(typeof imageProps.src === "string" ? imageProps.src : undefined) ||
        existingSources.some((source) => isOptimizedMagentoImageUrl(source.srcSet));

      if (isMagentoOptimized) {
        return renderNativePicture(imageProps, existingSources, className);
      }

      // Resolve dimensions from first valid source
      const firstSourceUrl = existingSources[0]!.srcSet;
      const defaultDimensions = resolveDimensions({
        url: firstSourceUrl,
        explicitWidth: imageProps.width,
        explicitHeight: imageProps.height,
        dict: stableImageDimensionsDict,
      });

      // No dimensions available - fallback to native picture tag
      if (!defaultDimensions) {
        return renderNativePicture(imageProps, existingSources, className);
      }

      // Optimize with Next.js getImageProps
      const common = {
        alt: imageProps.alt || "",
        className: imageProps.className,
        sizes: "100vw",
      };

      const optimizedSources = existingSources.map((source) => {
        const sourceDimensions = resolveDimensions({
          url: source.srcSet,
          dict: stableImageDimensionsDict,
        });

        // Use source dimensions if available, otherwise use default dimensions
        const dimensions = sourceDimensions || defaultDimensions;

        return {
          media: source.media,
          type: source.type,
          srcSet: getImageProps({
            ...common,
            src: source.srcSet,
            width: dimensions.width,
            height: dimensions.height,
          }).props.srcSet,
        };
      });

      // The default <img> is the fallback for viewports that match no <source>
      // (e.g. wide desktops). Optimize the explicit imageProps.src (typically the
      // large reference) so it is served through /_next/image instead of the raw CDN.
      const defaultSrc = typeof imageProps.src === "string" ? imageProps.src : existingSources[0]!.srcSet;
      const defaultSrcDimensions =
        resolveDimensions({
          url: defaultSrc,
          explicitWidth: imageProps.width,
          explicitHeight: imageProps.height,
          dict: stableImageDimensionsDict,
        }) ?? defaultDimensions;

      const defaultImageProps = getImageProps({
        ...common,
        src: defaultSrc,
        width: defaultSrcDimensions.width,
        height: defaultSrcDimensions.height,
      }).props;

      // img should not contain srcSet or sizes props, and must keep the optimized
      // src (drop the raw imageProps.src so it never overrides the optimized one).
      const { srcSet: _srcSet, sizes: _sizes, src: _src, ...cleanImgProps } = imageProps;
      const { srcSet: _defaultSrcSet, sizes: _defaultSizes, ...rest } = defaultImageProps;

      return (
        <picture className={className}>
          {optimizedSources.map((source, index) => (
            <source key={`${source.media}_${index}`} media={source.media} type={source.type} srcSet={source.srcSet} />
          ))}
          <img {...rest} {...cleanImgProps} alt={defaultImageProps.alt ?? ""} style={defaultImageProps.style} />
        </picture>
      );
    },
    [isNextImageDisabled, stableImageDimensionsDict]
  );

  return <PictureBehaviourContext.Provider value={renderPicture}>{children}</PictureBehaviourContext.Provider>;
};
